import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import isPlainObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'
import keys from 'lodash/keys'

import {
  dedupeWireValidationIssues,
  humanizeWireValidationMessage,
  type WireValidationIssue
} from '@klear/schema/wire/errors'

export type WireValidationErrorMap = Record<string, string>

function isWireValidationIssue(value: unknown): value is WireValidationIssue {
  if (!isPlainObject(value)) {
    return false
  }
  const record = value as Record<string, unknown>
  return isString(record.path) && isString(record.message)
}

function issuesFromCauseValue(cause: unknown): WireValidationIssue[] | undefined {
  if (!isObject(cause)) {
    return undefined
  }
  if (!isArray((cause as { issues?: unknown }).issues)) {
    return undefined
  }
  const issues = (cause as { issues: unknown[] }).issues.filter(isWireValidationIssue)
  return issues.length > 0 ? issues : undefined
}

export function wireValidationIssuesFromTrpcError(error: unknown): WireValidationIssue[] | undefined {
  if (!isObject(error)) {
    return undefined
  }
  const record = error as Record<string, unknown>

  const fromTopLevelCause = issuesFromCauseValue(record.cause)
  if (fromTopLevelCause) {
    return fromTopLevelCause
  }

  const data = record.data
  if (isPlainObject(data)) {
    const fromDataCause = issuesFromCauseValue((data as Record<string, unknown>).cause)
    if (fromDataCause) {
      return fromDataCause
    }
  }

  return undefined
}

function isBadRequestTrpcError(error: unknown): boolean {
  if (!isObject(error)) {
    return false
  }
  const data = (error as { data?: { code?: unknown } }).data
  return data?.code === 'BAD_REQUEST'
}

function errorMessage(error: unknown): string | undefined {
  if (!isObject(error)) {
    return undefined
  }
  const message = (error as { message?: unknown }).message
  return isString(message) && message.length > 0 ? message : undefined
}

export function wireValidationErrorMapFromMessage(message: string): WireValidationErrorMap | undefined {
  const segments = message
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
  if (segments.length === 0) {
    return undefined
  }

  const errors: WireValidationErrorMap = {}
  let matched = 0
  for (const segment of segments) {
    const separator = segment.indexOf(': ')
    if (separator === -1) {
      errors.root = segment
      matched++
      continue
    }
    const path = segment.slice(0, separator).trim()
    const fieldMessage = segment.slice(separator + 2).trim()
    if (!path || !fieldMessage) {
      continue
    }
    errors[path] = fieldMessage
    matched++
  }

  return matched > 0 ? errors : undefined
}

function stripPathPrefix(path: string, prefix: string): string {
  if (path === prefix) {
    return 'root'
  }
  if (path.startsWith(`${prefix}.`)) {
    return path.slice(prefix.length + 1)
  }
  return path
}

export function wireValidationErrorMapFromIssues(
  issues: readonly WireValidationIssue[],
  options?: { pathPrefix?: string }
): WireValidationErrorMap | undefined {
  const prefix = options?.pathPrefix ?? 'data'
  const errors: WireValidationErrorMap = {}
  for (const issue of dedupeWireValidationIssues(issues)) {
    const fieldPath = stripPathPrefix(issue.path, prefix)
    errors[fieldPath] = humanizeWireValidationMessage(issue.path, issue.message)
  }
  return keys(errors).length > 0 ? errors : undefined
}

export function wireValidationErrorMapFromTrpcError(
  error: unknown,
  options?: { pathPrefix?: string }
): WireValidationErrorMap | undefined {
  const issues = wireValidationIssuesFromTrpcError(error)
  if (issues) {
    return wireValidationErrorMapFromIssues(issues, options)
  }
  if (isBadRequestTrpcError(error)) {
    const message = errorMessage(error)
    if (message) {
      return wireValidationErrorMapFromMessage(message)
    }
  }
  return undefined
}
